import * as React from 'react';
import styled from 'styled-components';
import { pick } from 'lodash';
import { _ } from '@splunk/ui-utils/i18n';
import ImageContext from '@splunk/visualization-context/ImageContext';
import type ImageRegistry from '@splunk/visualization-context/ImageRegistry';
import { toDimension } from './style';

/**
 * check if an image is considered as remote image
 * @param {*} src
 */
export const isRemoteImage = (src = '') => {
    const [type] = src.split('://');
    return src.indexOf('://') > -1 && type !== 'http' && type !== 'https';
};

interface RemoteImageProps {
    imageURL: string;
    width?: number | string;
    height?: number | string;
    preserveAspectRatio: boolean;
    title: string;
}

interface RemoteImageState {
    preserveAspectRatio: boolean;
    imageDataURI: string | null;
}

interface ImageProps {
    shouldPreserveAspectRatio: boolean;
    width: number | string;
    height: number | string;
}

type ImageRegistryValue = ImageRegistry | null;

const toImageDimensions = props => {
    if (props.shouldPreserveAspectRatio) {
        return `
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
        `;
    }
    return toDimension(pick(props, ['width', 'height']));
};

const Image = styled.img<ImageProps>`
    display: block;
    ${props => toImageDimensions(props)};
`;

class RemoteImage extends React.PureComponent<RemoteImageProps, RemoteImageState> {
    static readonly contextType = ImageContext as React.Context<ImageRegistryValue>;

    declare context: ImageRegistryValue;

    constructor(props: RemoteImageProps) {
        super(props);
        this.state = {
            imageDataURI: null,
            preserveAspectRatio: Boolean(props.preserveAspectRatio),
        };
    }

    componentDidMount() {
        this.getImage(this.props.imageURL);
    }

    componentDidUpdate(prevProps: RemoteImageProps) {
        if (prevProps.imageURL !== this.props.imageURL) {
            this.getImage(this.props.imageURL);
        }
        if (prevProps.preserveAspectRatio !== this.props.preserveAspectRatio) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                preserveAspectRatio: this.props.preserveAspectRatio,
            });
        }
    }

    async getImage(imageURL: string) {
        const imageRegistry = this.context;
        if (imageRegistry) {
            try {
                const image = await imageRegistry.getByURL(imageURL);
                this.setState({ imageDataURI: image.dataURI });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }
    }

    render() {
        const { imageDataURI } = this.state;
        const { width, height, title } = this.props;

        if (!imageDataURI) {
            return null;
        }
        return (
            <Image
                src={imageDataURI}
                shouldPreserveAspectRatio={this.state.preserveAspectRatio}
                width={width}
                height={height}
                alt={_(title)}
                onError={e => {
                    (e.target as HTMLImageElement).alt = _('Image not found');
                }}
            />
        );
    }
}

export default RemoteImage;
